/**
 * Notification Hook - Interval Polling
 * 
 * This hook manages notifications by checking periodically.
 * Checks for new notifications every 5 minutes.
 * 
 * Usage:
 * const { unreadCount, notifications, markAsRead, markAllAsRead, refresh } = useNotifications();
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import UserManagmentService from '../Services/UsermanagmentService';

const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes (change to 10 * 60 * 1000 for 10 minutes)

export const useNotifications = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [latestId, setLatestId] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const pollingTimeoutRef = useRef(null);
  const isCheckingRef = useRef(false);

  // Show alert for new notifications
  const showNewNotificationAlert = useCallback((newNotifications) => {
    const count = newNotifications.length;
    const firstNotification = newNotifications[0];

    // Browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('New Notification', {
        body: firstNotification.message || `You have ${count} new notification(s)`,
        icon: '/favicon.ico',
        tag: 'notification-alert',
        requireInteraction: false,
      });
    }

    // Dispatch custom event for other components
    window.dispatchEvent(new CustomEvent('newNotification', {
      detail: { count, notifications: newNotifications }
    }));
  }, []);

  // Check for new notifications periodically
  const checkNewNotifications = useCallback(async () => {
    if (isCheckingRef.current || !isActive) return;

    isCheckingRef.current = true;
    setError(null);

    try {
      const response = await UserManagmentService.checkNewNotifications(latestId);
      const data = response?.data;
      
      if (data?.status) {
        // Always update unread count
        setUnreadCount(data.unread_count || 0);

        // If there are new notifications, prepend them to existing list
        if (data.has_new && data.notifications && data.notifications.length > 0) {
          setLatestId(data.latest_id);
          
          // Add new notifications to the top of the list
          setNotifications(prev => [...data.notifications, ...prev]);
          
          // Show alert for new notifications
          showNewNotificationAlert(data.notifications);
        }
      }
    } catch (err) {
      console.error('Failed to check new notifications:', err);
      setError(err?.response?.message || err?.message || 'Failed to check notifications');
    } finally {
      isCheckingRef.current = false;
      
      // Schedule next check
      if (isActive) {
        pollingTimeoutRef.current = setTimeout(checkNewNotifications, CHECK_INTERVAL);
      }
    }
  }, [latestId, isActive, showNewNotificationAlert]);

  // Fetch all notifications (only called manually or on initial load)
  const fetchAllNotifications = useCallback(async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await UserManagmentService.getNotifications(page);
      const data = response?.data;
      
      if (data?.status !== false && data?.notifications) {
        setNotifications(data.notifications);
        setUnreadCount(data.unread_count || 0);
        setCurrentPage(data.current_page || 1);
        setLastPage(data.last_page || 1);
        
        // Update latest ID
        if (data.notifications.length > 0) {
          const maxId = Math.max(...data.notifications.map(n => n.id));
          setLatestId(maxId);
        }
        
        setHasInitialLoad(true);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError(err?.response?.message || err?.message || 'Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load more notifications
  const loadMore = useCallback(async () => {
    if (isLoadingMore || currentPage >= lastPage) return;

    try {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      const response = await UserManagmentService.getNotifications(nextPage);
      const data = response?.data;
      
      if (data?.status !== false && data?.notifications) {
        // Append new notifications to existing list
        setNotifications(prev => [...prev, ...data.notifications]);
        setCurrentPage(data.current_page || nextPage);
        setLastPage(data.last_page || lastPage);
      }
    } catch (err) {
      console.error('Failed to load more notifications:', err);
      setError(err?.response?.message || err?.message || 'Failed to load more notifications');
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, lastPage, isLoadingMore]);

  // Mark notification as read (optimistic update - no full refresh)
  const markAsRead = useCallback(async (notificationId) => {
    // Optimistic update - update UI immediately
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, is_read: 1 }
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await UserManagmentService.markNotificationAsRead(notificationId);
    } catch (err) {
      console.error('Failed to mark as read:', err);
      setError(err?.response?.message || err?.message || 'Failed to mark as read');
      
      // Revert optimistic update on error
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: 0 }
            : notif
        )
      );
      setUnreadCount(prev => prev + 1);
      
      throw err;
    }
  }, []);

  // Mark all as read (optimistic update - no full refresh)
  const markAllAsRead = useCallback(async () => {
    // Store previous state for rollback
    const previousNotifications = [...notifications];
    const previousUnreadCount = unreadCount;

    // Optimistic update - update UI immediately
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, is_read: 1 }))
    );
    setUnreadCount(0);

    try {
      await UserManagmentService.markAllNotificationsAsRead();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      setError(err?.response?.message || err?.message || 'Failed to mark all as read');
      
      // Revert optimistic update on error
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
      
      throw err;
    }
  }, [notifications, unreadCount]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await UserManagmentService.deleteNotification(notificationId);

      // Update local state
      const deletedNotif = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      if (deletedNotif && !deletedNotif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
      setError(err?.response?.message || err?.message || 'Failed to delete notification');
      throw err;
    }
  }, [notifications]);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // Initialize and start polling
  useEffect(() => {
    // Initial fetch (only once)
    fetchAllNotifications();
    
    // Request notification permission
    requestNotificationPermission();

    // Cleanup
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Start interval polling after initial load
  useEffect(() => {
    if (hasInitialLoad && isActive) {
      // Start polling after initial data is loaded
      pollingTimeoutRef.current = setTimeout(() => {
        checkNewNotifications();
      }, CHECK_INTERVAL);

      return () => {
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
        }
      };
    }
  }, [hasInitialLoad, isActive, checkNewNotifications]);

  // Handle visibility change (pause when tab not visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsActive(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return {
    // State
    unreadCount,
    notifications,
    error,
    isActive,
    isLoading,
    isLoadingMore,
    hasMore: currentPage < lastPage,
    currentPage,
    lastPage,

    // Actions
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh: fetchAllNotifications,
    requestNotificationPermission,
    loadMore,
  };
};

export default useNotifications;
